import {FormattedMessage} from '@pinecast/i18n';
import type {CSS} from '@pinecast/styles';
import styled, {REDUCED_MOTION} from '@pinecast/styles';
import type {ColorMap} from '@pinecast/styles/colors';
import * as React from 'react';

import ScreenReaderOnly from './ScreenReaderOnly';

const DURATION = 1.5;

// The width of the moving highlight. It is a fixed number of pixels and not a
// share of the placeholder, so a wide card gets the same narrow highlight as a
// short line of text.
const BAND_WIDTH = 120;

// The highlight starts just outside the left edge and ends just outside the
// right edge. With a background as wide as the band, `calc(100% + BAND_WIDTH)`
// puts its left edge on the placeholder's right edge, whatever the width.
const animation = {
  from: {
    backgroundPosition: `${-BAND_WIDTH}px top`,
  },
  to: {
    backgroundPosition: `calc(100% + ${BAND_WIDTH}px) top`,
  },
} as any;

// The placeholder is `neutral`, and a band of `sheen` crosses it. Both are
// tokens with a value of their own in each color mode: a light gray with a
// light blue highlight, and a dark blue gray with a lighter one.
const shimmer = (
  designColors: ColorMap,
  neutralColor: string = designColors.badgeGrayBackground,
  sheenColor: string = designColors.borderBlueNormal,
) => ({
  animationDuration: `${DURATION}s`,
  animationName: animation,
  animationIterationCount: 'infinite',
  animationTimingFunction: 'linear',
  backgroundClip: 'padding-box',
  backgroundColor: neutralColor,
  // Square on, so that both ends of the band are `neutral`. At an angle, the
  // sheen reaches the corners of the band, and its edge shows.
  backgroundImage: `linear-gradient(90deg, ${neutralColor} 0%, ${sheenColor} 50%, ${neutralColor} 100%)`,
  // Where the highlight rests when nothing moves: outside the placeholder, so
  // the placeholder is a flat block.
  backgroundPosition: `${-BAND_WIDTH}px top`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: `${BAND_WIDTH}px 100%`,
  borderRadius: '4px',
  cursor: 'default',
  pointerEvents: 'none',
  userSelect: 'none',
  willChange: 'background-position',
  // Stopped, the placeholder holds still at its first frame.
  [REDUCED_MOTION]: {animationName: 'none'},

  '::before': {visibility: 'hidden'},
  '::after': {visibility: 'hidden'},
});

function useDelay() {
  const ref = React.useRef<HTMLSpanElement | HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    if (ref.current) {
      const left = ref.current.getBoundingClientRect().left;
      const delayRatio = left / window.innerWidth;
      ref.current.style.animationDelay = `${delayRatio * DURATION}s`;
    }
  }, []);
  return ref;
}

// A placeholder stands in for content that hasn't arrived, often inside the
// heading or the cell that content will fill. Said out loud, that is "loading"
// rather than an empty heading, with or without the shimmer.
const LoadingText = () => (
  <ScreenReaderOnly>
    <FormattedMessage description="Loading state" defaultMessage="Loading…" />
  </ScreenReaderOnly>
);

type CommonProps = {
  neutralColor?: string;
  sheenColor?: string;
  style?: CSS;
};

type InlineProps = CommonProps & {width?: string};

const InlineSkeletonBase = styled(
  'span',
  (
    {
      $neutralColor,
      $sheenColor,
      $width = '60%',
    }: {
      $neutralColor?: string;
      $sheenColor?: string;
      $width?: string;
    },
    designColors,
  ) => ({
    ...shimmer(designColors, $neutralColor, $sheenColor),
    display: 'inline-block',
    height: '1em',
    maxWidth: '160px',
    verticalAlign: 'middle',
    width: $width,
  }),
);

export const InlineSkeleton = (props: InlineProps) => {
  const ref = useDelay();
  return (
    <InlineSkeletonBase
      $neutralColor={props.neutralColor}
      $sheenColor={props.sheenColor}
      $width={props.width}
      ref={ref}
      style={props.style}
    >
      <LoadingText />
    </InlineSkeletonBase>
  );
};

const BlockSkeletonBase = styled(
  'div',
  (
    {
      $neutralColor,
      $sheenColor,
    }: {
      $neutralColor?: string;
      $sheenColor?: string;
    },
    designColors,
  ) => ({
    ...shimmer(designColors, $neutralColor, $sheenColor),
    display: 'block',
  }),
);

export const BlockSkeleton = (props: CommonProps) => {
  const ref = useDelay();
  return (
    <BlockSkeletonBase
      $neutralColor={props.neutralColor}
      $sheenColor={props.sheenColor}
      ref={ref as React.Ref<HTMLDivElement>}
      style={props.style}
    >
      <LoadingText />
    </BlockSkeletonBase>
  );
};
